import { useMemo } from "react";
import uniqueId from "lodash.uniqueid";
import { TFunction } from "react-i18next";
import * as Enumerable from "linq-es2015";
import * as yup from "yup";

import {
  ActionAttributeTypes,
  ActionNames,
  ActionParameters,
  Attribute,
  NotificationTemplate,
  RuleActionDto,
  RuleConditionDto,
  RuleConditionsGroupDto,
  RuleDto,
  RuleStatus,
  RuleTriggerValues,
  WorkflowActionData,
  WorkflowCondition,
} from "lib/api";
import { validateEmail } from "lib/utils/validation";
import { actionIsInvalid } from "components/workflow/AddEdit/Actions/utils";

export const useSessions = (
  t: TFunction<"workflowAddEdit">
): { label: string; value: string }[] => {
  return useMemo(
    (): Array<{ label: string; value: string }> => [
      { label: t("details.title"), value: "details" },
      { label: t("trigger.title"), value: "trigger" },
      { label: t("eventType.title"), value: "eventType" },
      { label: t("conditions.title"), value: "conditions" },
      { label: t("actions.title"), value: "actions" },
    ],
    [t]
  );
};

export const useValidationSchema = (
  t: TFunction<"workflowAddEdit">
): yup.AnySchema => {
  return useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t("validation.required")),
        eventTypeId: yup.string().trim().required(t("validation.required")),
      }),
    [t]
  );
};

export const RuleConditionsToFormConditions = (
  ruleConditions: RuleConditionsGroupDto[] | undefined
): WorkflowCondition[] => {
  const returnValue = ruleConditions?.map((rc, index) => {
    return rc.conditions.map((c) => {
      let range;
      let value;
      if (c.function === "range" && c.fields && c.fields.length >= 2) {
        range = [c.fields[0], c.fields[1]];
      } else if (c.type === "distance" && c.fields && c.fields.length >= 5) {
        range = ["Coordinate", c.fields[0], c.fields[1], c.fields[2]];
        // eslint-disable-next-line prefer-destructuring
        value = c.fields[3];
      } else if (c.fields && c.fields.length >= 1) {
        // eslint-disable-next-line prefer-destructuring
        value =
          c.type === "boolean" ? c.fields[0].toLocaleLowerCase() : c.fields[0];
      }

      return {
        groupId: rc.group_order,
        // the global operator has same and/or value for each groups (the top and/or logic). to set the correct group condition we are using the local_operator
        groupLogic:
          index === 0
            ? rc.global_operator
            : Enumerable.AsEnumerable<RuleConditionDto>(
                rc.conditions ?? []
              ).FirstOrDefault()?.local_operator ?? rc.global_operator,
        property: c.type === "distance" ? "location" : c.property,
        displayLabel: c.type === "distance" ? "Distance" : c.display_label,
        operator: c.function,
        type: c.type,
        rangevalues: range,
        value,
      } as WorkflowCondition;
    });
  });
  return returnValue?.flat() ?? [];
};

export const FormConditionsToRuleConditions = (
  formConditions: WorkflowCondition[] | undefined
): RuleConditionsGroupDto[] => {
  const conditions = Enumerable.asEnumerable(
    (formConditions as WorkflowCondition[]) ?? []
  );
  // first condition group logic is used as condition beetween different groups
  const firstCondition = conditions.FirstOrDefault();
  const returnValue = conditions.GroupBy(
    (key) => key.groupId,
    (elem) => elem,
    (key, elem) => {
      const elems: Enumerable.Enumerable<WorkflowCondition> =
        Enumerable.asEnumerable<WorkflowCondition>(elem);
      return {
        group_name: "",
        global_operator: firstCondition?.groupLogic ?? "",
        group_order: key,
        conditions: elems
          .Select((el: WorkflowCondition) => {
            let fields: string[];
            const value =
              el.type === "text" && typeof el.value === "string"
                ? el.value.trim()
                : el.value;
            if (el.operator === "range") {
              fields = [...(el.rangevalues as string[])];
            } else if (el.type === "distance") {
              fields = [
                // first rangeValue is static text "Coordinate"
                ...(el.rangevalues?.slice(1) as string[]),
                (value as string) ?? "",
                "miles",
              ];
            } else if (el.type === "boolean") {
              fields =
                value !== undefined && value !== null
                  ? [(value as boolean).toString()]
                  : [];
            } else if (value !== undefined) {
              fields = [value as string];
            } else {
              fields = [""];
            }
            return {
              display_label:
                el.type === "distance" ? undefined : el.displayLabel,
              function: el.operator,
              local_operator: el.groupLogic,
              property: el.type === "distance" ? undefined : el.property,
              type: el.type,
              fields,
            } as RuleConditionDto;
          })
          .ToArray(),
      } as RuleConditionsGroupDto;
    }
  );
  return returnValue.ToArray();
};

export const RuleActionToFormAction = (
  ruleAction: RuleActionDto[] | undefined,
  templateEmail: NotificationTemplate[] | undefined,
  templatePush: NotificationTemplate[] | undefined,
  templateSms: NotificationTemplate[] | undefined
): WorkflowActionData[] => {
  const result = ruleAction?.map((rule) => {
    switch (rule.type) {
      case ActionNames.NotificationEmail: {
        const emails = (rule.parameters[ActionParameters.to] ?? "")
          .concat(
            rule.parameters[ActionParameters.cc] ?? "",
            rule.parameters[ActionParameters.bcc] ?? ""
          )
          .split(";")
          .filter((str) => str !== undefined && str !== "");
        // if string is a guid then is user id otherwise is a mail
        const users: Array<Partial<User>> = emails.map((e) => {
          return {
            id: validateEmail(e) ? undefined : e,
            email: validateEmail(e) ? e : undefined,
          };
        });
        const action = {
          id: uniqueId(),
          isInvalid: false,
          name: rule.type,
          label: "NotificationEmail",
          continueOnError: false,
          continueOnNoResult: false,
          exe_order: rule.priority,
          notification_template_id:
            rule.parameters[ActionParameters.emailTemplateId],
          attributes: {
            [ActionAttributeTypes.Email]: {
              label: "",
              type: "array",
              value: users,
            } as Attribute,
          },
        } as WorkflowActionData;
        return {
          ...action,
          isInvalid: actionIsInvalid(
            action,
            templateEmail,
            templatePush,
            templateSms
          )[0],
        } as WorkflowActionData;
      }
      default:
        return undefined;
    }
  });
  return result
    ? Enumerable.asEnumerable(result)
        .Where((action) => action !== undefined)
        .OrderBy((act) => act?.exe_order ?? 0)
        .Select((act) => act as WorkflowActionData)
        .ToArray()
    : [];
};

export const FormActionToRuleAction = (
  wfAction: WorkflowActionData[]
): RuleActionDto[] => {
  const result = wfAction.map((action, index) => {
    switch (action.name) {
      case ActionNames.NotificationEmail: {
        if (
          action.attributes[ActionAttributeTypes.Email] &&
          action.attributes[ActionAttributeTypes.Email].value &&
          Array.isArray(action.attributes[ActionAttributeTypes.Email].value)
        ) {
          const users = action.attributes[ActionAttributeTypes.Email]
            .value as Array<Partial<User>>;
          const to = users
            .map((u) => {
              return (u.id ?? u.email) as string;
            })
            .filter((u) => u !== undefined);
          return {
            type: ActionNames.NotificationEmail,
            priority: index,
            parameters: {
              [ActionParameters.emailTemplateId]:
                action.notification_template_id,
              [ActionParameters.useHtml]: "true",
              [ActionParameters.to]: to.join(";"),
              [ActionParameters.cc]: "",
              [ActionParameters.bcc]: "",
            },
          } as RuleActionDto;
        }
        break;
      }
      default:
        return undefined;
    }
    return undefined;
  });
  return result
    .filter((action) => action !== undefined)
    .map((act) => act as RuleActionDto);
};

export const formDataToWorkflow = (
  data: WorkflowFormData
): Omit<RuleDto, "id"> => {
  return {
    name: data.name.trim(),
    description: data.description?.trim() ?? "",
    type_id: data.eventTypeId,
    status: data.active ? RuleStatus.Active : RuleStatus.Inactive,
    priority: 0,
    trigger: 1,
    input_parameters: {
      conditions_group: FormConditionsToRuleConditions(data.conditions),
      actions: FormActionToRuleAction(data.actions),
    },
    template: { is_template: false },
    schedule: { is_scheduled: false },
  };
};

export const useInitialValues = (
  data: RuleDto | undefined,
  templateEmail: NotificationTemplate[] | undefined,
  templatePush: NotificationTemplate[] | undefined,
  templateSms: NotificationTemplate[] | undefined
): WorkflowFormData => {
  return useMemo(
    () => ({
      name: data?.name ?? "",
      description: data?.description ?? "",
      eventTypeId: data?.type_id ?? "",
      trigger: data?.trigger ?? RuleTriggerValues.Creation,
      active: !data?.status == null || data?.status === RuleStatus.Active,
      conditions: RuleConditionsToFormConditions(
        data?.input_parameters?.conditions_group
      ),
      // rule: data?.wf_rule ?? "",
      // distanceRule: data?.wf_distance_rule ?? [],
      actions: RuleActionToFormAction(
        data?.input_parameters?.actions,
        templateEmail,
        templatePush,
        templateSms
      ),
    }),
    [
      data?.description,
      data?.input_parameters?.actions,
      data?.input_parameters?.conditions_group,
      data?.name,
      data?.status,
      data?.trigger,
      data?.type_id,
      templateEmail,
      templatePush,
      templateSms,
    ]
  );
};
